import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, Award, Truck, ShieldCheck, Star } from "lucide-react";
import { api } from "../lib/api";
import HolographicPlaceholder from "../components/HolographicPlaceholder";
import ProductCard from "../components/ProductCard";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.slice(0, 8))).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 pt-16 pb-24 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-8 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#B026FF]/30 bg-[#B026FF]/10 backdrop-blur text-[11px] tracking-[0.22em] uppercase text-[#E1B7FF] font-bold">
              <Sparkles size={12} /> The Pokémon Vault, Reimagined
            </div>
            <h1 className="mt-6 font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-white">
              Build the<br />
              collection of <span className="text-gradient-violet">your dreams.</span>
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-xl leading-relaxed">
              Sealed booster boxes, alt-art singles, plushies, and the gear collectors trust.
              Every order earns Kavo Points — redeem them for credit on your next purchase.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/shop/booster-boxes"
                data-testid="hero-cta-shop-boosters"
                className="btn-violet h-12 px-7 rounded-md font-medium tracking-wide inline-flex items-center gap-2"
              >
                Shop Booster Boxes <ArrowRight size={16} />
              </Link>
              <Link
                to="/shop/single-cards"
                data-testid="hero-cta-shop-singles"
                className="btn-ghost-violet h-12 px-7 rounded-md font-medium tracking-wide inline-flex items-center gap-2"
              >
                Browse Singles
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "10K+", l: "Cards Listed" },
                { v: "4.9★", l: "Trustpilot" },
                { v: "48h", l: "Avg Ship" },
              ].map((s, i) => (
                <div key={i} className="border-l border-[#B026FF]/30 pl-4">
                  <div className="font-mono text-2xl text-white">{s.v}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero side ornament */}
          <div className="hidden lg:block col-span-12 lg:col-span-4 relative h-[420px] lg:h-[520px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-full h-full">
                <div className="absolute inset-0 rounded-full bg-[#7A28CB]/20 blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-[#B026FF]/20" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-[#B026FF]/10" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] rounded-full border border-[#B026FF]/5" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display font-extrabold text-[10rem] leading-none text-transparent" style={{ WebkitTextStroke: "1px rgba(176,38,255,0.35)" }}>K</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="py-6 border-y border-white/5 bg-[#0A0812] ribbon-mask">
        <div className="flex animate-marquee whitespace-nowrap gap-16 text-[11px] tracking-[0.4em] uppercase text-white/40 font-mono">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex gap-16 items-center">
              <span className="flex items-center gap-3"><Truck size={14} className="text-[#B026FF]" /> Free Shipping over £50</span>
              <span className="text-white/20">◆</span>
              <span className="flex items-center gap-3"><ShieldCheck size={14} className="text-[#B026FF]" /> Authenticated Sealed Product</span>
              <span className="text-white/20">◆</span>
              <span className="flex items-center gap-3"><Award size={14} className="text-[#B026FF]" /> Collector Grade Singles</span>
              <span className="text-white/20">◆</span>
              <span className="flex items-center gap-3"><Sparkles size={14} className="text-[#B026FF]" /> Earn Kavo Points on every order</span>
              <span className="text-white/20">◆</span>
              <span className="flex items-center gap-3"><Zap size={14} className="text-[#B026FF]" /> Worldwide Shipping</span>
              <span className="text-white/20">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Shop by category</div>
            <h2 className="mt-3 font-display font-light text-4xl lg:text-5xl tracking-tight text-white">Built for the collection.</h2>
          </div>
          <Link to="/shop" data-testid="categories-view-all" className="hidden md:inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="card-elev rounded-xl p-5 group animate-fade-up opacity-0"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <HolographicPlaceholder label={c.name} subLabel="Browse" ratio="aspect-[5/3]" />
              <div className="pt-5">
                <h3 className="font-display text-xl text-white">{c.name}</h3>
                <p className="text-sm text-white/50 mt-1 line-clamp-2">{c.description}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-[#B026FF] text-sm font-medium group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Featured</div>
            <h2 className="mt-3 font-display font-light text-4xl lg:text-5xl tracking-tight text-white">This week's chases.</h2>
          </div>
          <Link to="/shop" data-testid="featured-view-all" className="hidden md:inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            Shop all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* LOYALTY BANNER */}
      <section className="max-w-[1400px] mx-auto px-6 pb-24">
        <div className="card-elev rounded-2xl p-10 lg:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#B026FF]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#7A28CB]/20 rounded-full blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold flex items-center gap-2">
                <Star size={12} /> Kavo Points
              </div>
              <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl tracking-tight text-white">
                Every pull pays you back.
              </h2>
              <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
                Earn 1 Kavo Point per pound spent. Redeem 100 points for £5 off any order — stackable
                with sales, no expiry. New trainers get 100 points on signup.
              </p>
              <Link
                to="/shop"
                data-testid="loyalty-cta"
                className="mt-8 inline-flex btn-ghost-violet h-12 px-7 rounded-md font-medium tracking-wide items-center gap-2"
              >
                Start earning <ArrowRight size={16} />
              </Link>
            </div>
            <div className="font-mono text-white/80 space-y-3 lg:pl-10">
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span>£50 spent</span><span className="text-[#B026FF]">+ 50 pts</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span>£200 booster box</span><span className="text-[#B026FF]">+ 200 pts</span>
              </div>
              <div className="flex justify-between border-b border-white/5 pb-3">
                <span>Redeem 200 pts</span><span className="text-white">- £10</span>
              </div>
              <div className="flex justify-between text-lg pt-2">
                <span className="text-white">Lifetime savings</span>
                <span className="text-gradient-violet font-bold">unlimited</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
